/* eslint-disable @typescript-eslint/no-explicit-any */
import { put, call, select, all, takeEvery } from 'redux-saga/effects'
import { Layer } from '@carto/kepler.gl/layers'
import { Map, MapDataset, MapType, CartoMapLayer } from 'features/maps/types'
import { AttributeStats, AttributeStatsRequest } from '../types'
import { shouldLoadAsDynamicTiling } from './builderSliceUtils'
import {
  addDatasetAttributeStatsOnRequest,
  loadAttributeStatsRequest,
  loadHexColumnStatsRequest,
  removeDatasetAttributeStatsOnRequest,
  showGlobalBuilderError
} from './builderSlice'
import { CHANNEL_SCALES } from '@carto/kepler.gl/constants'
import { PayloadAction } from '@reduxjs/toolkit'
import { getAttributeStats } from '../api/builder'
import { getClient } from 'features/common/utils/clientParameter'
import { updateTilesetDatasetAttributeStats, updateTilesetHexColumnStats } from './keplerGlActions'
import { getDatasetById } from 'features/maps/state/mapsSlice'

export function* loadAttributeStatsForDynamicTiling(
  currentMap: Map,
  currentMapDatasets: MapDataset[],
  cacheMaxAgeInMinutes?: number
) {
  const layers = currentMap.keplerMapConfig?.config?.visState.layers || []
  try {
    // We don't use yield all to avoid repeated request
    for (const layer of layers) {
      const dataset = currentMapDatasets.find((d) => d.id === layer.config.dataId)

      if (dataset && shouldLoadAsDynamicTiling({ type: dataset.type, format: dataset.format })) {
        yield loadLayerAttributeStats({ layer, cacheMaxAgeInMinutes })
      }
    }
  } catch (e: any) {
    console.error('Error loading layer attributes stats', e)
    yield put(showGlobalBuilderError(e.message))
  }
}

function* loadLayerAttributeStats({
  layer,
  cacheMaxAgeInMinutes,
  noCache
}: {
  layer: CartoMapLayer
  cacheMaxAgeInMinutes?: number
  noCache?: boolean
}) {
  try {
    // We don't use yield all to avoid repeated request
    for (const channel of Object.keys(layer.visualChannels)) {
      const visualChannel = layer.visualChannels[channel]
      if (!visualChannel || typeof visualChannel !== 'object') continue

      const { name: attribute, channelScaleType, colorColumn } = visualChannel
      const datasetId = layer.config.dataId

      yield fetchLayerAttributeStats({
        datasetId,
        attribute,
        channelScaleType,
        colorColumn,
        cacheMaxAgeInMinutes,
        noCache
      })
    }
  } catch (e: any) {
    console.error('Error loading layer attributes stats', e)
    yield put(showGlobalBuilderError(e.message))
  }
}

export function* loadKeplerVisLayerAttributeStats({
  layer,
  cacheMaxAgeInMinutes,
  noCache
}: {
  layer: Layer
  cacheMaxAgeInMinutes?: number
  noCache?: boolean
}) {
  try {
    // We don't use yield all to avoid repeated request
    for (const channel of Object.keys(layer.visualChannels)) {
      const visualChannel = layer.visualChannels[channel]
      if (!visualChannel || typeof visualChannel !== 'object') continue

      const { field, channelScaleType } = layer.visualChannels[channel]

      if (!layer.config[field]) continue

      const { id: attribute, colorColumn } = layer.config[field]
      const datasetId = layer.config.dataId

      if (!datasetId) continue

      yield fetchLayerAttributeStats({
        datasetId,
        attribute,
        channelScaleType,
        colorColumn,
        cacheMaxAgeInMinutes,
        noCache
      })
    }
  } catch (e: any) {
    console.error('Error loading layer attributes stats')
    yield put(showGlobalBuilderError(e.message))
  }
}

function* fetchLayerAttributeStats({
  datasetId,
  attribute,
  channelScaleType,
  colorColumn,
  cacheMaxAgeInMinutes,
  noCache
}: {
  datasetId: string
  attribute: string
  channelScaleType?: string
  colorColumn?: string
  cacheMaxAgeInMinutes?: number
  noCache?: boolean
}) {
  try {
    if (colorColumn) {
      yield put(
        loadHexColumnStatsRequest({
          datasetId,
          attribute: colorColumn,
          cacheMaxAgeInMinutes,
          noCache
        })
      )
    }
    if (attribute && channelScaleType !== CHANNEL_SCALES.identity) {
      yield put(loadAttributeStatsRequest({ datasetId, attribute, cacheMaxAgeInMinutes, noCache }))
    }
  } catch (e: any) {
    console.error('Error loading layer attributes stats', e)
    yield put(showGlobalBuilderError(e.message))
  }
}

function* loadAttributeStats(action: PayloadAction<AttributeStatsRequest>) {
  const { datasetId, attribute, maxNumberOfCategories = 20, noCache } = action.payload

  let restoreStatsOnRequest = false
  try {
    const alreadyRequested = yield select((state) =>
      state.builder.datasetAttributeStatsOnRequest[datasetId]?.includes(attribute)
    )

    if (alreadyRequested && !noCache) {
      return
    }
    const mapDataset: MapDataset = yield select((state) =>
      state.maps.currentMapDatasets.find((d) => d.id === datasetId)
    )
    const { connectionName: connection = '', source, queryParameters, type, accessToken } = mapDataset

    yield put(addDatasetAttributeStatsOnRequest({ datasetId, attribute }))
    restoreStatsOnRequest = true
    const stats: AttributeStats = yield call(getAttributeStats, {
      connection,
      accessToken,
      source,
      queryParameters,
      attribute,
      type,
      client: getClient(),
      noCache,
      cacheMaxAgeInMinutes: action.payload.cacheMaxAgeInMinutes,
      maxNumberOfCategories
    })
    yield put(updateTilesetDatasetAttributeStats({ datasetId, stats }))
  } catch (error: any) {
    console.error('loadAttributeStats', error)
    const msg = `Error getting attribute stats: ${error.message}`
    yield put(showGlobalBuilderError(msg))
  } finally {
    if (restoreStatsOnRequest) {
      yield put(removeDatasetAttributeStatsOnRequest({ datasetId, attribute }))
    }
  }
}

function* handleLoadHexColumnStats(action: PayloadAction<AttributeStatsRequest>) {
  const { datasetId, attribute, maxNumberOfCategories = 1000, cacheMaxAgeInMinutes } = action.payload

  try {
    const mapDataset: MapDataset | undefined = yield select((state) => getDatasetById(state, datasetId))
    if (!mapDataset || mapDataset.type === MapType.tileset) return

    const { connectionName: connection = '', source, queryParameters, type, accessToken } = mapDataset

    const stats: AttributeStats = yield call(getAttributeStats, {
      connection,
      accessToken,
      source,
      queryParameters,
      attribute,
      type,
      client: getClient(),
      cacheMaxAgeInMinutes,
      maxNumberOfCategories
    })

    yield put(updateTilesetHexColumnStats({ datasetId, stats }))
  } catch (error: any) {
    const msg = `Error getting hex column stats: ${error.message}`
    console.error(`handleLoadHexColumnStats: ${msg}`)
    yield put(showGlobalBuilderError(msg))
  }
}

function* watchMap() {
  yield takeEvery(loadAttributeStatsRequest.type, loadAttributeStats)
  yield takeEvery(loadHexColumnStatsRequest.type, handleLoadHexColumnStats)
}

export default function* statsSagas() {
  try {
    yield all([watchMap()])
  } catch (e: any) {
    //TODO
  }
}
